import axios, { AxiosHeaders, CanceledError, HttpStatusCode } from "axios";
import AttorneyHubAPIService from "common/services/api/attorney-hub-api-service";
import Guid from "common/values/guid/guid";
import { UnauthorizedEntityMemberAccessError } from "legal-entities/entities/entity-member/entity-member";
import UpdateLegalEntityAPIRequest from "legal-entities/entities/legal-entity/api/request-contracts/update-legal-entity-api-request";
import LegalEntityAPIResponse from "legal-entities/entities/legal-entity/api/response-contracts/legal-entity-api-response";
import LegalEntity, { LegalEntityNotFoundError } from "legal-entities/entities/legal-entity/legal-entity";
import Session from "users/session/session";


export default class LegalEntityAPIService {
    private readonly headers: AxiosHeaders = new AxiosHeaders();

    constructor(session: Readonly<Session>) {
        if (!session.authToken) {
            throw new Error("Cannot create LegalEntityAPIService without session.");
        }
        this.headers.set("Authorization", `Bearer ${session.authToken.value}`);
    }

    async getLegalEntityById(
        legalEntityId: Guid,
        abortController?: AbortController
    ): Promise<LegalEntity> {
        const url = new URL(
            `/legal-entities/${legalEntityId.toString()}`,
            AttorneyHubAPIService.apiBaseUrl
        );
        try {
            const response = await axios.get(
                url.toString(),
                {
                    headers: this.headers,
                    signal: abortController?.signal
                }
            );
            const responseData = Object.assign(new LegalEntityAPIResponse(), response.data);
            const legalEntity = responseData.deserialize();
            return legalEntity;
        } catch (error) {
            if (error instanceof CanceledError)
                throw error;
            if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
                throw new LegalEntityNotFoundError(legalEntityId);
            if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Forbidden)
                throw new UnauthorizedEntityMemberAccessError(legalEntityId);
            throw new LegalEntityAPIServiceError("getLegalEntityById", error);
        }
    }

    async getUserLegalEntities(
        userId: Guid,
        abortController?: AbortController
    ): Promise<LegalEntity[]> {
        const url = new URL(
            `/legal-entities/`,
            AttorneyHubAPIService.apiBaseUrl
        );
        try {
            const response = await axios.get(
                url.toString(),
                {
                    headers: this.headers,
                    signal: abortController?.signal
                }
            );

            let responseData: LegalEntityAPIResponse[] = [];
            Object.assign(responseData, response.data);
            const legalEntities: LegalEntity[] = [];
            for (const entityData of responseData) {
                const legalEntityData = Object.assign(new LegalEntityAPIResponse(), entityData);
                legalEntities.push(legalEntityData.deserialize());
            }

            return legalEntities;
        } catch (error) {
            if (error instanceof CanceledError)
                throw error;
            if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
                throw new LegalEntityNotFoundError(userId);
            if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Forbidden)
                throw new UnauthorizedEntityMemberAccessError(userId);
            throw new LegalEntityAPIServiceError("getUserLegalEntities", error);
        }
    }

    async updateLegalEntity(
        originalEntity: LegalEntity,
        updatedEntity: LegalEntity,
        abortController?: AbortController
    ): Promise<LegalEntity> {
        const url = new URL(
            `/legal-entities/${originalEntity.id.value}`,
            AttorneyHubAPIService.apiBaseUrl
        );

        try {
            const response = await axios.patch(
                url.toString(),
                new UpdateLegalEntityAPIRequest(originalEntity, updatedEntity).payload,
                {
                    headers: this.headers.concat({ 'Content-Type': 'application/json' }),
                    signal: abortController?.signal
                }
            );
            const responseData = Object.assign(new LegalEntityAPIResponse(), response.data);
            return responseData.deserialize();
        } catch (error) {
            if (error instanceof CanceledError)
                throw error;
            if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
                throw new LegalEntityNotFoundError(originalEntity.id);
            if (axios.isAxiosError(error) && error.response?.status === HttpStatusCode.Forbidden)
                throw new UnauthorizedEntityMemberAccessError(originalEntity.id);
            throw new LegalEntityAPIServiceError("patchLegalEntity", error);
        }
    }
}

export class LegalEntityAPIServiceError extends Error {
    constructor(methodName: string, innerError: unknown) {
        super(`LegalEntityAPIService.${methodName} failed: ${innerError instanceof Error ?
            innerError.message : String(innerError)}`
        );
        this.name = "LegalEntityAPIServiceError";
    }
}